import FullCalendar, {
  type CalendarRef,
  type DateSelectInfo,
  type DayHeaderInfo,
  type EventClickInfo,
  type EventDisplayInfo,
  type EventDropInfo,
  type EventResizeDoneInfo,
} from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/react/daygrid'
import interactionPlugin from '@fullcalendar/react/interaction'
import '@fullcalendar/react/skeleton.css'
import classicThemePlugin from '@fullcalendar/react/themes/classic'
import '@fullcalendar/react/themes/classic/palette.css'
import '@fullcalendar/react/themes/classic/theme.css'
import timeGridPlugin from '@fullcalendar/react/timegrid'
import { useCallback, useMemo, useRef, useState } from 'react'

import { useScheduleI18n } from '../../i18n'
import type { ScheduleEvent, ScheduleEventId } from '../../types'
import { dsText, fontSize, tokenVar } from '../../utils'
import {
  getWeeklyHoursEvents,
  processScheduleEvents,
  toFullCalendarEvents,
} from '../../utils/scheduleEvent'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { defaultCalendarConfig } from './calendarConfig'

export interface WeeklyHoursCalendarProps {
  /** Schedule events from the API */
  events: ScheduleEvent[]
  /** Schedule timezone */
  scheduleTimeZone: string
  /** Whether editing is allowed */
  canEdit?: boolean
  /** Whether deletion is allowed */
  canDelete?: boolean
  /** Called when a new time slot is selected - can return Promise to delay selection clear */
  onSelect?: (start: Date, end: Date) => Promise<void> | void
  /** Called when an event is clicked */
  onEventClick?: (event: ScheduleEvent) => void
  /** Called when an event is moved/resized */
  onEventChange?: (eventId: ScheduleEventId, start: Date, end: Date) => void
  /** Called when an event delete button is clicked */
  onEventDelete?: (eventId: ScheduleEventId) => void
  /** Additional calendar options */
  calendarOptions?: Partial<typeof defaultCalendarConfig>
  /** Custom class name */
  className?: string
}

/**
 * Weekly hours calendar component using FullCalendar React
 * Displays FREE (open) time slots for a schedule
 */
export const WeeklyHoursCalendar = ({
  events,
  scheduleTimeZone,
  canEdit = false,
  canDelete = false,
  onSelect,
  onEventClick,
  onEventChange,
  onEventDelete,
  calendarOptions = {},
  className = '',
}: WeeklyHoursCalendarProps) => {
  const { text } = useScheduleI18n()
  const calendarRef = useRef<CalendarRef>(null)
  const [pendingCreateSelection, setPendingCreateSelection] = useState<{
    start: Date
    end: Date
  } | null>(null)
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false)
  const [pendingDeleteEventId, setPendingDeleteEventId] =
    useState<ScheduleEventId | null>(null)

  // Process events and filter to weekly hours only
  const calendarEvents = useMemo(() => {
    const helpers = processScheduleEvents(events, scheduleTimeZone)
    const weeklyHours = getWeeklyHoursEvents(helpers)
    return toFullCalendarEvents(weeklyHours)
  }, [events, scheduleTimeZone])

  const dayHeaderLabels = useMemo(
    () => [
      text('schedule.common.weekdayShort.sun', 'Sun'),
      text('schedule.common.weekdayShort.mon', 'Mon'),
      text('schedule.common.weekdayShort.tue', 'Tue'),
      text('schedule.common.weekdayShort.wed', 'Wed'),
      text('schedule.common.weekdayShort.thu', 'Thu'),
      text('schedule.common.weekdayShort.fri', 'Fri'),
      text('schedule.common.weekdayShort.sat', 'Sat'),
    ],
    [text],
  )

  const dayHeaderContent = useCallback(
    (arg: DayHeaderInfo) => dayHeaderLabels[arg.date.getDay()],
    [dayHeaderLabels],
  )

  // Handle time slot selection (create new event)
  const handleSelect = useCallback(
    (selectInfo: DateSelectInfo) => {
      if (!canEdit || !onSelect) return

      setPendingCreateSelection({
        start: new Date(selectInfo.start),
        end: new Date(selectInfo.end),
      })
    },
    [canEdit, onSelect],
  )

  const handleCloseCreateConfirmation = useCallback(() => {
    setPendingCreateSelection(null)
    calendarRef.current?.getApi().unselect()
  }, [])

  const handleConfirmCreate = useCallback(async () => {
    if (!pendingCreateSelection || !onSelect) return

    setIsSubmittingCreate(true)
    try {
      await onSelect(pendingCreateSelection.start, pendingCreateSelection.end)
    } finally {
      setIsSubmittingCreate(false)
      setPendingCreateSelection(null)
      calendarRef.current?.getApi().unselect()
    }
  }, [onSelect, pendingCreateSelection])

  // Handle event click
  const handleEventClick = useCallback(
    (clickInfo: EventClickInfo) => {
      if (!onEventClick) return

      const eventId = clickInfo.event.id
      const originalEvent = events.find((e) => String(e.id) === eventId)

      if (originalEvent) {
        onEventClick(originalEvent)
      }
    },
    [events, onEventClick],
  )

  const getEventId = useCallback(
    (calendarEventId: string): ScheduleEventId | undefined =>
      events.find((event) => String(event.id) === calendarEventId)?.id,
    [events],
  )

  // Handle event drag/drop
  const handleEventDrop = useCallback(
    (dropInfo: EventDropInfo) => {
      if (!canEdit || !onEventChange) {
        dropInfo.revert()
        return
      }

      const eventId = getEventId(dropInfo.event.id)
      if (eventId !== undefined && dropInfo.event.start && dropInfo.event.end) {
        onEventChange(eventId, dropInfo.event.start, dropInfo.event.end)
      }
    },
    [canEdit, getEventId, onEventChange],
  )

  // Handle event resize
  const handleEventResize = useCallback(
    (resizeInfo: EventResizeDoneInfo) => {
      if (!canEdit || !onEventChange) {
        resizeInfo.revert()
        return
      }

      const eventId = getEventId(resizeInfo.event.id)
      if (
        eventId !== undefined &&
        resizeInfo.event.start &&
        resizeInfo.event.end
      ) {
        onEventChange(eventId, resizeInfo.event.start, resizeInfo.event.end)
      }
    },
    [canEdit, getEventId, onEventChange],
  )

  const dayNames = useMemo(
    () => [
      text('schedule.common.weekdayLong.sun', 'Sunday'),
      text('schedule.common.weekdayLong.mon', 'Monday'),
      text('schedule.common.weekdayLong.tue', 'Tuesday'),
      text('schedule.common.weekdayLong.wed', 'Wednesday'),
      text('schedule.common.weekdayLong.thu', 'Thursday'),
      text('schedule.common.weekdayLong.fri', 'Friday'),
      text('schedule.common.weekdayLong.sat', 'Saturday'),
    ],
    [text],
  )

  // Custom event content with delete button
  const renderEventContent = useCallback(
    (eventInfo: EventDisplayInfo) => {
      const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (canDelete && onEventDelete) {
          setPendingDeleteEventId(getEventId(eventInfo.event.id) ?? null)
        }
      }

      // Intl range formatting uses an en dash; keep the legacy hyphen
      const timeText = eventInfo.timeText.replace(/\s*[–—]\s*/, ' - ')
      const eventStart = eventInfo.event.start
      const eventEnd = eventInfo.event.end
      const eventDurationMinutes =
        eventStart && eventEnd
          ? Math.round((eventEnd.getTime() - eventStart.getTime()) / 60000)
          : null
      const rawTitle = eventInfo.event.title?.trim()
      const normalizedTitle =
        !rawTitle || rawTitle.toLowerCase() === 'open'
          ? text('schedule.status.open', 'Open')
          : rawTitle
      const shouldShowTitle =
        Boolean(normalizedTitle) &&
        (eventDurationMinutes === null || eventDurationMinutes >= 105)

      return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div
            style={{
              fontFamily: dsText.Body3.fontFamily,
              fontSize: fontSize(dsText.Body3.fontSize),
              fontWeight: dsText.Body3_Bold.fontWeight,
              lineHeight: dsText.Body3.lineHeight,
              color: tokenVar('brand.fg'),
            }}
          >
            {timeText}
          </div>
          {shouldShowTitle && (
            <div
              style={{
                fontFamily: dsText.Body3.fontFamily,
                fontSize: fontSize(dsText.Body3.fontSize),
                fontWeight: dsText.Body3.fontWeight,
                lineHeight: dsText.Body3.lineHeight,
                color: tokenVar('brand.fg'),
              }}
            >
              {normalizedTitle}
            </div>
          )}
          {canDelete && (
            <button
              type="button"
              className="event-delete"
              aria-label={text(
                'schedule.weeklyHours.calendar.deleteEventLabel',
                'Delete {{day}} {{time}}',
                {
                  day: eventStart ? (dayNames[eventStart.getDay()] ?? '') : '',
                  time: timeText,
                },
              )}
              onClick={handleDeleteClick}
              onKeyDown={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: 0,
                right: 4,
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontWeight: 'bold',
                color: tokenVar('brand.fg'),
              }}
            >
              <span aria-hidden="true">X</span>
            </button>
          )}
        </div>
      )
    },
    [canDelete, dayNames, getEventId, onEventDelete, text],
  )

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteEventId !== null && onEventDelete) {
      onEventDelete(pendingDeleteEventId)
    }
    setPendingDeleteEventId(null)
  }, [onEventDelete, pendingDeleteEventId])

  const mergedOptions = {
    ...defaultCalendarConfig,
    ...calendarOptions,
    selectable: canEdit,
    editable: canEdit,
    dayHeaderContent,
  }

  return (
    <>
      <div
        className={`schedule-calendar ${className}`}
        style={{ width: '100%' }}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[
            classicThemePlugin,
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
          ]}
          {...mergedOptions}
          events={calendarEvents}
          select={handleSelect}
          eventClick={onEventClick ? handleEventClick : undefined}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventContent={renderEventContent}
        />
      </div>
      <DeleteConfirmationModal
        isOpen={pendingCreateSelection !== null}
        onClose={handleCloseCreateConfirmation}
        onConfirm={handleConfirmCreate}
        title={text(
          'schedule.weeklyHours.calendar.confirmCreateTitle',
          'Create schedule entry',
        )}
        description={text(
          'schedule.weeklyHours.calendar.confirmCreateDescription',
          'Create a new schedule entry for this selected time range?',
        )}
        cancelLabel={text('schedule.common.cancel', 'Cancel')}
        confirmLabel={text('schedule.common.create', 'Create')}
        confirmTone="primary"
        isSubmitting={isSubmittingCreate}
      />
      <DeleteConfirmationModal
        isOpen={pendingDeleteEventId !== null}
        onClose={() => setPendingDeleteEventId(null)}
        onConfirm={handleConfirmDelete}
        title={text(
          'schedule.weeklyHours.calendar.confirmDeleteTitle',
          'Delete time slot',
        )}
      />
    </>
  )
}
